import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrash } from '@fortawesome/free-solid-svg-icons';
import { faFilePdf, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { API_CONFIG, buildApiUrl } from '../config/api';

function UstanoveList() {
  const [ustanove, setUstanove] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(API_CONFIG.ENDPOINTS.USTANOVE, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Neuspješno dohvaćanje ustanova.');
        const data = await res.json();
        setUstanove(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Da li ste sigurni da želite obrisati ustanovu?');
    if (!confirmed) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_CONFIG.ENDPOINTS.USTANOVE}/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok && res.status !== 204) throw new Error('Brisanje nije uspjelo.');
      setUstanove((prev) => prev.filter((x) => x.id_ustanova !== id));
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDownload = async (format) => {
    const token = localStorage.getItem('token');
    const url = buildApiUrl(API_CONFIG.ENDPOINTS.USTANOVE, { format, ...(searchQuery.trim() && { q: searchQuery.trim() }) });
    try {
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error('Neuspješno preuzimanje.');
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = format === 'pdf' ? 'lista-ustanova.pdf' : 'lista-ustanova.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      alert(e.message);
    }
  };

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return ustanove;
    return ustanove.filter((u) => {
      const naziv = String(u.naziv_ustanove || '').toLowerCase();
      const mjesto = String(u.mjesto || '').toLowerCase();
      return naziv.includes(q) || mjesto.includes(q);
    });
  }, [ustanove, searchQuery]);

  // Determine role before any early returns to keep hooks order consistent
  const currentUser = useMemo(() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; } }, []);

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const page = Math.min(currentPage, totalPages);
  const startIndex = (page - 1) * itemsPerPage;
  const visible = filtered.slice(startIndex, startIndex + itemsPerPage);

  if (loading) return <div className="flex items-center justify-center min-h-[50vh] text-gray-600">Učitavanje...</div>;
  if (error) return (
    <div className="max-w-3xl mx-auto p-4">
      <div className="p-3 mb-4 text-sm text-red-700 bg-red-100 rounded-lg">Greška: {error}</div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-2xl font-semibold text-gray-900">Lista ustanova</h2>
        <div className="w-full sm:w-80">
          <input
            type="text"
            placeholder="Pretraga (naziv, mjesto)"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
        {(currentUser?.role === 'admin' || currentUser?.role === 'user') && (
          <Link to="/new-ustanova" className="inline-flex">
            <button className="px-4 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">Dodaj novu ustanovu</button>
          </Link>
        )}
        <div className="inline-flex items-center gap-2">
          <button onClick={() => handleDownload('pdf')} className="px-3 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 transition-colors inline-flex items-center gap-2" title="Preuzmi PDF">
            <FontAwesomeIcon icon={faFilePdf} />
            PDF
          </button>
          <button onClick={() => handleDownload('xlsx')} className="px-3 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors inline-flex items-center gap-2" title="Preuzmi Excel">
            <FontAwesomeIcon icon={faFileExcel} />
            Excel
          </button>
        </div>
      </div>
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Naziv ustanove</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mjesto</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Akcije</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-gray-500">Nema zapisa.</td>
                </tr>
              ) : (
                visible.map((u) => (
                  <tr key={u.id_ustanova} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{u.naziv_ustanove}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{u.mjesto}</td>
                    <td className="px-4 py-3 text-sm text-right">
                      <div className="inline-flex gap-2">
                        {(currentUser?.role === 'admin' || currentUser?.role === 'user') && (
                          <>
                            <Link to={`/ustanove/${u.id_ustanova}/edit`}>
                              <button className="px-3 py-2 rounded-md bg-amber-600 text-white hover:bg-amber-700 transition-colors" title="Uredi">
                                <FontAwesomeIcon icon={faPenToSquare} />
                              </button>
                            </Link>
                            {(currentUser?.role === 'admin' || currentUser?.role === 'user') && (
                              <button onClick={() => handleDelete(u.id_ustanova)} className="px-3 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors" title="Obriši">
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="px-1 sm:px-0 flex items-center justify-between text-sm text-gray-700">
        <div>Stranica {page} od {totalPages}</div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={page === 1} className={`px-3 py-1 rounded-md border ${page === 1 ? 'text-gray-400 border-gray-200' : 'hover:bg-gray-50 border-gray-300'}`}>Prethodna</button>
          <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className={`px-3 py-1 rounded-md border ${page === totalPages ? 'text-gray-400 border-gray-200' : 'hover:bg-gray-50 border-gray-300'}`}>Sljedeća</button>
        </div>
      </div>
    </div>
  );
};

export default UstanoveList;
